import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { useState, useEffect, useLayoutEffect } from 'react'
import { useHotkeys } from '@mantine/hooks'
import { usePage } from '@/components/providers/site/PageProvider'
import { useLocalStorage } from '@/core/hooks/useLocalStorage'
import { useQuery } from '@/core/hooks/useQuery'
import { RcTrans } from '@/components/atoms/RcTrans'
import { RcIconButton } from '@/components/molecules/interactive/RcIconButton'

export const Joyrider = ({
  external,
  localKey,
  prompt,
  steps
}: {
  external?: Date
  localKey: string
  prompt?: string
  steps: {
    target: string
    title: string
    content: string
  }[]
}) => {
  const [dismissed, setDismissed] = useLocalStorage({
    key: localKey,
    defaultValue: false
  })
  const [step, setStep] = useState(0)
  const [run, setRun] = useState(false)
  const [rect, setRect] = useState<DOMRect>()
  const { open, close } = usePage()

  const query = useQuery()
  const learn = !!query.get('learn')

  // start if ?learn=true
  useEffect(() => {
    if (learn) {
      setRun(true)
    }
  }, [learn])

  // start if parent says so
  useEffect(() => {
    if (external) {
      setRun(true)
    }
  }, [external])

  // press ESC to stop
  useHotkeys([
    [
      'escape',
      () => {
        setRun(false)
        setStep(0)
      }
    ]
  ])

  // prompt user to learn if prompt provided
  useEffect(() => {
    if (!dismissed && !learn && prompt) {
      open(
        <Button
          size='small'
          onClick={() => {
            setDismissed(true)
            close()
            setRun(true)
          }}
        >
          <RcTrans i18nKey='shared.lets-do-it' />
        </Button>,
        prompt,
        15000,
        () => {
          setDismissed(true)
        }
      )
    }
  }, [open, close, dismissed, setDismissed, learn, prompt, steps])

  useLayoutEffect(() => {
    const update = () => {
      const target = document.querySelector(steps[step].target)

      if (target && run) {
        target.scrollIntoView({
          behavior: 'instant',
          block: 'center',
          inline: 'center'
        })
        setRect(target.getBoundingClientRect())
      }
    }

    window.addEventListener('scroll', update, false)
    window.addEventListener('resize', update, false)
    update()

    return () => {
      window.removeEventListener('scroll', update, false)
      window.removeEventListener('resize', update, false)
    }
  }, [steps, step, run])

  if (!rect || !run) return null

  return (
    <Box
      sx={theme => ({
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: theme.palette.common.black,
        opacity: 0.7,
        zIndex: theme.zIndex.drawer,
        width: '100%',
        height: '100%',
        mixBlendMode: 'soft-light'
      })}
    >
      <Tooltip
        open
        title={
          <Stack
            direction='column'
            spacing={3}
            pt={1}
            pb={2}
            position='relative'
          >
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='flex-start'
            >
              <Typography variant='h3'>{steps[step].title}</Typography>

              <RcIconButton
                icon={['fal', 'times']}
                size='small'
                onClick={() => setRun(false)}
                sx={{ mt: -1, mr: -1 }}
              />
            </Stack>
            <Typography>{steps[step].content}</Typography>

            {steps.length > 1 ? (
              <Stack direction='row' spacing={2} justifyContent='flex-end'>
                <Button
                  disabled={step === 0}
                  size='small'
                  variant='outlined'
                  onClick={() => {
                    setStep(step - 1)
                  }}
                >
                  <RcTrans i18nKey='shared.back' />
                </Button>
                {step === steps.length - 1 ? (
                  <Button
                    size='small'
                    onClick={() => {
                      setRun(false)
                      setStep(0)
                    }}
                  >
                    <RcTrans i18nKey='shared.finish' />
                  </Button>
                ) : (
                  <Button
                    size='small'
                    onClick={() => {
                      setStep(step + 1)
                    }}
                  >
                    <RcTrans i18nKey='shared.next' />
                  </Button>
                )}
              </Stack>
            ) : null}
          </Stack>
        }
      >
        <Box
          onClick={() => {
            setRun(false)
            document.querySelector<HTMLElement>(steps[step].target)?.click?.()
          }}
          sx={{
            cursor: 'pointer',
            position: 'fixed',
            top: rect?.top!,
            left: rect?.left!,
            width: rect?.width!,
            height: rect?.height!,
            backgroundColor: 'grey',
            opacity: 1,
            borderRadius: 5
          }}
        />
      </Tooltip>
    </Box>
  )
}
